import type { Language } from "./translations";

export interface RouteSeo {
  title: Record<Language, string>;
  description: Record<Language, string>;
  h1: Record<Language, string>;
  support: Record<Language, string>;
}

// Homepage SEO
export const homeSeo: RouteSeo = {
  title: {
    pt: "Gestão contínua da superfície de ataque externa | SentinelHub",
    en: "Continuous external attack surface management | SentinelHub",
    es: "Gestión continua de la superficie de ataque externa | SentinelHub",
  },
  description: {
    pt: "Monitore portas, serviços, CVEs, DNS e certificados dos ativos autorizados da sua empresa com uma plataforma brasileira de EASM.",
    en: "Monitor ports, services, CVEs, DNS and certificates of your company's authorized assets with a Brazilian EASM platform.",
    es: "Monitoree puertos, servicios, CVEs, DNS y certificados de los activos autorizados de su empresa con una plataforma brasileña de EASM.",
  },
  h1: {
    pt: "Gestão contínua da superfície de ataque externa",
    en: "Continuous external attack surface management",
    es: "Gestión continua de la superficie de ataque externa",
  },
  support: {
    pt: "Identifique vulnerabilidades, serviços expostos e falhas de configuração antes que sejam explorados. Sem agentes e somente em ativos próprios ou formalmente autorizados.",
    en: "Identify vulnerabilities, exposed services and misconfigurations before they are exploited. Agentless and only on assets you own or are formally authorized to test.",
    es: "Identifique vulnerabilidades, servicios expuestos y fallos de configuración antes de que sean explotados. Sin agentes y solo en activos propios o formalmente autorizados.",
  },
};

export const insightsSeo: RouteSeo = {
  title: {
    pt: "Insights de Segurança e Gestão de Exposição | SentinelHub",
    en: "Security Insights | SentinelHub",
    es: "Insights de Seguridad | SentinelHub",
  },
  description: {
    pt: "Guias técnicos e análises sobre EASM, hardening, TLS, DNS, WAF e gestão de vulnerabilidades escritos pela equipe SentinelHub.",
    en: "Technical guides and analysis on EASM, hardening, TLS, DNS, WAF and vulnerability management written by the SentinelHub team.",
    es: "Guías técnicas y análisis sobre EASM, hardening, TLS, DNS, WAF y gestión de vulnerabilidades escritas por el equipo SentinelHub.",
  },
  h1: {
    pt: "Insights de segurança e gestão de exposição externa",
    en: "Security insights and external exposure management",
    es: "Insights de seguridad y gestión de exposición externa",
  },
  support: {
    pt: "Guias práticos escritos pela equipe SentinelHub para ajudar times de segurança a reduzir a superfície de ataque externa.",
    en: "Practical guides by the SentinelHub team to help security teams reduce their external attack surface.",
    es: "Guías prácticas por el equipo SentinelHub para ayudar a equipos de seguridad a reducir la superficie de ataque externa.",
  },
};

export const disclosureSeo: RouteSeo = {
  title: {
    pt: "Divulgação Responsável de Vulnerabilidades | SentinelHub",
    en: "Responsible Vulnerability Disclosure Policy | SentinelHub",
    es: "Divulgación Responsable de Vulnerabilidades | SentinelHub",
  },
  description: {
    pt: "Política de divulgação responsável de vulnerabilidades da SentinelHub: como reportar, escopo, testes permitidos e compromissos.",
    en: "SentinelHub's responsible vulnerability disclosure policy: how to report, scope, allowed testing and commitments.",
    es: "Política de divulgación responsable de vulnerabilidades de SentinelHub: cómo reportar, alcance, pruebas permitidas y compromisos.",
  },
  h1: {
    pt: "Política de Divulgação Responsável de Vulnerabilidades",
    en: "Responsible Vulnerability Disclosure Policy",
    es: "Política de Divulgación Responsable de Vulnerabilidades",
  },
  support: {
    pt: "Como reportar vulnerabilidades de forma segura à SentinelHub.",
    en: "How to report vulnerabilities securely to SentinelHub.",
    es: "Cómo reportar vulnerabilidades de forma segura a SentinelHub.",
  },
};

export const threatMapSeo: RouteSeo = {
  title: {
    pt: "Mapa Global de Ameaças — Dados Reais, Sem Simulação | SentinelHub",
    en: "Global Threat Map — Real Data, No Simulation | SentinelHub",
    es: "Mapa Global de Amenazas — Datos Reales, Sin Simulación | SentinelHub",
  },
  description: {
    pt: "Ataques registrados hoje pelos sensores do SANS ISC e CVEs sob exploração ativa do catálogo CISA KEV. Cada número traz sua janela: dia corrente parcial ou dia fechado. Sem estimativas e sem alvos fictícios.",
    en: "Attacks recorded today by SANS ISC sensors and CVEs under active exploitation from the CISA KEV catalog. Every figure states its window: partial current day or closed day. No estimates, no fictitious targets.",
    es: "Ataques registrados hoy por los sensores del SANS ISC y CVEs bajo explotación activa del catálogo CISA KEV. Cada número indica su ventana: día en curso parcial o día cerrado. Sin estimaciones ni objetivos ficticios.",
  },
  h1: {
    pt: "Mapa global de ameaças com dados reais",
    en: "Global threat map with real data",
    es: "Mapa global de amenazas con datos reales",
  },
  support: {
    pt: "Dados agregados de fontes abertas auditáveis: eventos observados pelo SANS ISC/DShield, vulnerabilidades sob exploração ativa confirmada no catálogo CISA KEV e geolocalização dos IPs de origem. Atualizado de hora em hora; as contagens do dia corrente reiniciam à meia-noite UTC. Não exibimos alvos porque as fontes não informam quem foi atacado.",
    en: "Aggregated data from auditable open sources: events observed by SANS ISC/DShield, vulnerabilities under confirmed active exploitation in the CISA KEV catalog, and geolocation of source IPs. Updated hourly; current-day counts reset at midnight UTC. We show no targets because the sources do not disclose who was attacked.",
    es: "Datos agregados de fuentes abiertas auditables: eventos observados por SANS ISC/DShield, vulnerabilidades bajo explotación activa confirmada en el catálogo CISA KEV y geolocalización de las IPs de origen. Actualizado cada hora; los conteos del día en curso se reinician a medianoche UTC. No mostramos objetivos porque las fuentes no informan quién fue atacado.",
  },
};
